import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  productCount,
  isMobileFilterOpen,
  onCloseMobileFilter 
}) => {
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'performance', label: 'Performance Enhancement' },
    { value: 'recovery', label: 'Recovery & Healing' },
    { value: 'cognitive', label: 'Cognitive Enhancement' }
  ];

  const sortOptions = [
    { value: 'popular', label: 'Most Popular' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'newest', label: 'Newest Arrivals' },
    { value: 'name-asc', label: 'Name: A to Z' },
    { value: 'name-desc', label: 'Name: Z to A' }
  ];

  const dosageOptions = [
    { value: 'all', label: 'All Dosages' },
    { value: '2mg', label: '2mg' },
    { value: '5mg', label: '5mg' },
    { value: '10mg', label: '10mg' },
    { value: '20mg', label: '20mg' }
  ];

  const brandOptions = [
    { value: 'all', label: 'All Brands' },
    { value: 'peptide-sciences', label: 'Peptide Sciences' },
    { value: 'research-labs', label: 'Research Labs' },
    { value: 'bio-peptides', label: 'Bio Peptides' },
    { value: 'advanced-research', label: 'Advanced Research' }
  ];

  const handlePriceChange = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    onFilterChange('priceRange', {
      ...filters?.priceRange,
      [field]: numValue
    });
  };

  return (
    <div className={`
      fixed lg:relative inset-0 lg:inset-auto z-[1030] lg:z-auto
      bg-background lg:bg-transparent
      transform transition-transform duration-300 lg:transform-none
      ${isMobileFilterOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      <div className="h-full lg:h-auto overflow-y-auto lg:overflow-visible">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-lg md:text-xl font-bold text-foreground">Filters</h2>
          <button
            onClick={onCloseMobileFilter}
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-250"
            style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
          >
            <Icon name="X" size={20} color="var(--color-error)" />
          </button>
        </div>

        {/* Filter Content */}
        <div className="p-4 md:p-6 lg:p-0 space-y-4 md:space-y-6">
          {/* Results Count & Clear */}
          <div className="flex items-center justify-between p-4 md:p-6 rounded-xl"
            style={{ 
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
              border: '1px solid var(--color-border)'
            }}>
            <div>
              <p className="text-xs md:text-sm text-muted-foreground mb-1">Showing Results</p>
              <p className="text-xl md:text-2xl font-bold gradient-text">{productCount}</p>
            </div>
            <button
              onClick={onClearFilters}
              className="px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium text-foreground transition-all duration-250 hover:scale-105"
              style={{ 
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}
            >
              Clear All
            </button>
          </div>

          {/* Sort By */}
          <div className="p-4 md:p-6 rounded-xl space-y-3 md:space-y-4"
            style={{ 
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)'
            }}>
            <div className="flex items-center gap-2 md:gap-3">
              <Icon name="ArrowUpDown" size={18} color="var(--color-primary)" />
              <h3 className="text-sm md:text-base font-semibold text-foreground">Sort By</h3>
            </div>
            <Select
              options={sortOptions}
              value={filters?.sortBy}
              onChange={(value) => onFilterChange('sortBy', value)}
              placeholder="Select sorting"
            />
          </div>

          {/* Category Filter */}
          <div className="p-4 md:p-6 rounded-xl space-y-3 md:space-y-4"
            style={{ 
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)'
            }}>
            <div className="flex items-center gap-2 md:gap-3">
              <Icon name="Grid3x3" size={18} color="var(--color-primary)" />
              <h3 className="text-sm md:text-base font-semibold text-foreground">Category</h3>
            </div>
            <Select
              options={categories}
              value={filters?.category}
              onChange={(value) => onFilterChange('category', value)}
              placeholder="Select category"
            />
          </div>

          {/* Price Range */}
          <div className="p-4 md:p-6 rounded-xl space-y-3 md:space-y-4"
            style={{ 
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)'
            }}>
            <div className="flex items-center gap-2 md:gap-3">
              <Icon name="DollarSign" size={18} color="var(--color-primary)" />
              <h3 className="text-sm md:text-base font-semibold text-foreground">Price Range</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <Input
                type="number"
                label="Min Price"
                placeholder="$0"
                value={filters?.priceRange?.min}
                onChange={(e) => handlePriceChange('min', e?.target?.value)}
              />
              <Input
                type="number"
                label="Max Price"
                placeholder="$500"
                value={filters?.priceRange?.max}
                onChange={(e) => handlePriceChange('max', e?.target?.value)}
              />
            </div>
          </div>

          {/* Dosage Strength */}
          <div className="p-4 md:p-6 rounded-xl space-y-3 md:space-y-4"
            style={{ 
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)'
            }}>
            <div className="flex items-center gap-2 md:gap-3">
              <Icon name="Pill" size={18} color="var(--color-primary)" />
              <h3 className="text-sm md:text-base font-semibold text-foreground">Dosage Strength</h3>
            </div>
            <Select
              options={dosageOptions}
              value={filters?.dosage}
              onChange={(value) => onFilterChange('dosage', value)}
              placeholder="Select dosage"
            />
          </div>

          {/* Availability */}
          <div className="p-4 md:p-6 rounded-xl space-y-3 md:space-y-4"
            style={{ 
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)'
            }}>
            <div className="flex items-center gap-2 md:gap-3">
              <Icon name="Package" size={18} color="var(--color-primary)" />
              <h3 className="text-sm md:text-base font-semibold text-foreground">Availability</h3>
            </div>
            <div className="space-y-2 md:space-y-3">
              <Checkbox
                label="In Stock Only"
                checked={filters?.inStock}
                onChange={(e) => onFilterChange('inStock', e?.target?.checked)}
              />
              <Checkbox
                label="Pre-Order Available"
                checked={filters?.preOrder}
                onChange={(e) => onFilterChange('preOrder', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Research Applications */}
          <div className="p-4 md:p-6 rounded-xl space-y-3 md:space-y-4"
            style={{ 
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)'
            }}>
            <div className="flex items-center gap-2 md:gap-3">
              <Icon name="FlaskConical" size={18} color="var(--color-primary)" />
              <h3 className="text-sm md:text-base font-semibold text-foreground">Research Applications</h3>
            </div>
            <div className="space-y-2 md:space-y-3">
              <Checkbox
                label="Muscle Growth"
                checked={filters?.applications?.muscleGrowth}
                onChange={(e) => onFilterChange('applications', { ...filters?.applications, muscleGrowth: e?.target?.checked })}
              />
              <Checkbox
                label="Recovery"
                checked={filters?.applications?.recovery}
                onChange={(e) => onFilterChange('applications', { ...filters?.applications, recovery: e?.target?.checked })}
              />
              <Checkbox
                label="Anti-Aging"
                checked={filters?.applications?.antiAging}
                onChange={(e) => onFilterChange('applications', { ...filters?.applications, antiAging: e?.target?.checked })}
              />
              <Checkbox
                label="Cognitive"
                checked={filters?.applications?.cognitive}
                onChange={(e) => onFilterChange('applications', { ...filters?.applications, cognitive: e?.target?.checked })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;